import { Folder, FolderPlus, Plus, Search } from 'lucide-react';
import { EmptyStatePrompt } from '@/components/core/common/feedback/EmptyStatePrompt';

export type MyLearningEmptyVariant = 'search' | 'folder' | 'root';

export interface MyLearningEmptyStateProps {
  variant: MyLearningEmptyVariant;
  /** Echoed back in the copy. Only read by the `search` variant. */
  searchTerm?: string;
  onClearSearch?: () => void;
  onNewSubfolder?: () => void;
  onNewLesson?: () => void;
}

/**
 * The three ways this page can be empty, which are three different problems and
 * get three different exits:
 *
 * - `search` — the query matched nothing. The only useful action is undoing it.
 * - `folder` — a real but empty folder. Both exits create something *here*, so
 *   the new lesson link carries the folder id forward.
 * - `root` — a new account. Routes out to the composer via `EmptyStatePrompt`,
 *   the page-level empty pattern, rather than the `EmptyState` primitive.
 *
 * Which one renders is the caller's decision: the page knows whether a search
 * is active and whether it is at root, and this component deliberately does not
 * re-derive that.
 */
export function MyLearningEmptyState({
  variant,
  searchTerm,
  onClearSearch,
  onNewSubfolder,
  onNewLesson,
}: MyLearningEmptyStateProps) {
  if (variant === 'search') {
    return (
      <div className='py-16 text-center'>
        <div className='mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-muted'>
          <Search className='h-8 w-8 text-muted-foreground' />
        </div>
        <h2 className='mb-2 text-lg font-medium text-foreground'>
          No results for &quot;{searchTerm}&quot;
        </h2>
        <p className='mx-auto mb-6 max-w-md text-muted-foreground'>
          Try adjusting your search or clearing filters.
        </p>
        <button
          onClick={onClearSearch}
          className='inline-flex items-center rounded-lg bg-foreground px-4 py-2 text-background transition-colors hover:bg-foreground/90 max-sm:min-h-11'
        >
          Clear search
        </button>
      </div>
    );
  }

  if (variant === 'folder') {
    return (
      <div className='py-16 text-center'>
        <div className='mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-brand-50 dark:bg-muted'>
          <Folder className='h-8 w-8 text-brand-600 dark:text-primary' />
        </div>
        <h2 className='mb-2 text-lg font-medium text-foreground'>
          This folder is empty
        </h2>
        <p className='mx-auto mb-6 max-w-md text-muted-foreground'>
          Add a new subfolder, or create a lesson and it will be saved here.
        </p>
        <div className='flex justify-center gap-3'>
          <button
            type='button'
            onClick={onNewSubfolder}
            className='inline-flex items-center rounded-lg bg-foreground px-4 py-2 font-medium text-background transition-transform duration-200 hover:scale-[1.04] max-sm:min-h-11'
          >
            <FolderPlus className='mr-2 h-4 w-4' />
            New subfolder
          </button>
          <button
            type='button'
            onClick={onNewLesson}
            className='inline-flex items-center rounded-lg bg-foreground px-4 py-2 font-medium text-background transition-transform duration-200 hover:scale-[1.04] max-sm:min-h-11'
          >
            <Plus className='mr-2 h-4 w-4' />
            New lesson
          </button>
        </div>
      </div>
    );
  }

  return (
    <EmptyStatePrompt
      title='No learning content found'
      description='Create lessons or explore topics to view your learning progress here.'
      primaryAction={{ label: 'Start Learning', path: '/' }}
    />
  );
}
